import React, { useEffect, useMemo } from 'react'
import classNames from 'classnames/bind'
import styles from './index.module.scss'
import { Button, Dialog } from '@mui/material'
import PropTypes from 'prop-types'
import { XMarkIcon } from '~/assets/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { calcServingTimeBuffet } from '~/utils'
import { useTranslation } from 'react-i18next'
import { path } from '~/routes/path'
import STORAGE_KEYS from '~/constants/localStorage'
import { useDispatch, useSelector } from 'react-redux'
import { fetchDetailRestaurant, selectRestaurant } from '~/redux/restaurantSlice'
import { EXTERNAL_PARTNER } from '~/constants'
import { checkForNewBuffet, checkHasBuffetRequest } from '~/utils/buffet'
import { fetchMenu } from '~/views/Menu/slices/menuSlice'
import { selectPopupTopPage } from '~/redux/popupTopPageSlice'

const cx = classNames.bind(styles)

const getLastBuffetRequest = (buffet_requests_time) => {
  const lastIndex = buffet_requests_time.length - 1
  return buffet_requests_time[lastIndex]
}

// Component hiển thị thông báo bắt đầu buffet
const PopupStartBuffet = ({ open, setOpen }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const location = useLocation()
  const { pathname } = location

  const { open: openPopupTopPage } = useSelector(selectPopupTopPage)
  const { externalPartner, buffet_requests_time, restaurantSlug, tableUid, restaurantId } =
    useSelector(selectRestaurant)
  const isPartnerSmaregi = externalPartner === EXTERNAL_PARTNER.Smaregi
  const isTokenRedirectPath = `/${restaurantSlug}/${tableUid}` === pathname

  const hasBuffetRequestTime = useMemo(
    () => checkHasBuffetRequest(buffet_requests_time),
    [buffet_requests_time],
  )

  const minutesBuffet = useMemo(() => {
    if (hasBuffetRequestTime) {
      const lastestItem = getLastBuffetRequest(buffet_requests_time)
      return calcServingTimeBuffet(lastestItem.serving_ended_at)
    }
    return 0
  }, [buffet_requests_time, hasBuffetRequestTime])

  const locale = localStorage.getItem(STORAGE_KEYS.LOCALE)

  useEffect(() => {
    if (
      isTokenRedirectPath ||
      pathname === path.rescanQR ||
      pathname === path.introduction ||
      !locale
    )
      return

    if (isPartnerSmaregi && hasBuffetRequestTime) {
      checkForNewBuffet(buffet_requests_time, setOpen)
    }
  }, [
    isPartnerSmaregi,
    pathname,
    isTokenRedirectPath,
    hasBuffetRequestTime,
    locale,
    openPopupTopPage,
    buffet_requests_time,
    setOpen,
  ])

  useEffect(() => {
    if (!isPartnerSmaregi) return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        dispatch(
          fetchDetailRestaurant({
            restaurantSlug,
            tableUid,
          }),
        )
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [dispatch, isPartnerSmaregi, restaurantSlug, tableUid])

  const handleClose = () => setOpen(false)

  const handleSeeMenu = () => {
    if (pathname === path.indexMenu) {
      dispatch(fetchMenu(restaurantId))
    }
    navigate(path.indexMenu)
    setOpen(false)
  }

  const renderCloseBtn = () => (
    <Button className={cx('close_btn')} onClick={handleClose}>
      <XMarkIcon size="14" />
    </Button>
  )

  const renderContent = () => (
    <div className={cx('content')}>
      <div className={cx('name')}>{t('popup_start_buffet.course_menu_has_started')}</div>
      <div className={cx('time_label')}>{t('popup_start_buffet.time_limit')}</div>
      <strong className={cx('time_value')}>
        {t('popup_start_buffet.time_value', { value: minutesBuffet })}
      </strong>
      <Button className={cx('see_menu_btn')} onClick={handleSeeMenu}>
        {t('popup_start_buffet.see_menu')}
      </Button>
    </div>
  )

  if (pathname === path.introduction) return null

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="Start buffet"
      aria-describedby="Start buffet popup"
      classes={{
        paper: cx('paper'),
      }}>
      {renderCloseBtn()}
      {renderContent()}
    </Dialog>
  )
}

PopupStartBuffet.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
}

export default React.memo(PopupStartBuffet)
